import { useCallback, useEffect, useState } from "react";
import { ADMIN_EVENT_METRICS } from "../../../../../constants/api";
import { eventApi } from "../../utils/eventApi";
import { logEventDebug } from "../../utils/eventMessages";
import {
  METRICS_PAGE_LIMIT,
  getEventExpirationDate,
  getLastEvaluatedKey,
  getMetricsPayload,
  normalizeMetricRow,
} from "../utils/rankingMetrics";

export const useRankingMetrics = ({ event, open }) => {
  const [rankingRows, setRankingRows] = useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [isLoadingMoreMetrics, setIsLoadingMoreMetrics] = useState(false);
  const [hasLoadingError, setHasLoadingError] = useState(false);

  const loadRankingMetrics = useCallback(
    async (nextKey = null) => {
      const expirationDate = getEventExpirationDate(event);

      if (!expirationDate) {
        setRankingRows([]);
        setLastEvaluatedKey(null);
        setHasLoadingError(true);
        return;
      }

      try {
        if (nextKey) {
          setIsLoadingMoreMetrics(true);
        } else {
          setIsLoadingMetrics(true);
        }

        setHasLoadingError(false);

        const params = {
          expiration_date: expirationDate,
          limit: METRICS_PAGE_LIMIT,
        };

        if (nextKey?.statistic) {
          params.statistic = nextKey.statistic;
        }

        const response = await eventApi.get(ADMIN_EVENT_METRICS, {
          params,
        });

        logEventDebug("Métricas recebidas do backend:", response.data);

        const metricsPayload = getMetricsPayload(response.data);

        setRankingRows((currentRows) => {
          const baseIndex = nextKey ? currentRows.length : 0;
          const nextRows = metricsPayload.map((metric, index) =>
            normalizeMetricRow(metric, baseIndex + index)
          );

          return nextKey ? [...currentRows, ...nextRows] : nextRows;
        });
        setLastEvaluatedKey(getLastEvaluatedKey(response.data));
      } catch (error) {
        logEventDebug("Erro ao buscar métricas do ranking: ", error);

        if (!nextKey) {
          setRankingRows([]);
        }

        setLastEvaluatedKey(null);
        setHasLoadingError(true);
      } finally {
        setIsLoadingMetrics(false);
        setIsLoadingMoreMetrics(false);
      }
    },
    [event]
  );

  useEffect(() => {
    if (!open) {
      setRankingRows([]);
      setLastEvaluatedKey(null);
      setHasLoadingError(false);
      return;
    }

    loadRankingMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, loadRankingMetrics, open]);

  return {
    rankingRows,
    lastEvaluatedKey,
    isLoadingMetrics,
    isLoadingMoreMetrics,
    hasLoadingError,
    loadRankingMetrics,
  };
};
